import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { OAUTH_CANCELLED } from '../lib/capacitorAuth';
import { useAuth } from '../context/AuthContext';
import { supabase, supabasePublic, getEmailConfirmRedirect } from '../lib/supabase';
import { Mail, Lock, User, ArrowRight, Loader2, Calendar, AlertTriangle, CheckCircle, XCircle, Send, RefreshCw } from 'lucide-react';
import { PROFANITY_LIST } from '../lib/contentFilter';
import { trackEvent } from '../lib/posthog';

// Username validation function
function validateUsername(username, isUsernameReserved) {
  const cleaned = username.trim();
  
  // Check minimum length (except grandfathered 'erik')
  if (cleaned.length > 0 && cleaned.length < 3 && cleaned.toLowerCase() !== 'erik') {
    return { valid: false, error: 'Username must be at least 3 characters' };
  }
  
  // Check maximum length
  if (cleaned.length > 25) {
    return { valid: false, error: 'Username must be 25 characters or less' };
  }
  
  // Check for spaces
  if (cleaned.includes(' ')) {
    return { valid: false, error: 'Username cannot contain spaces' };
  }
  
  // Check for valid characters (alphanumeric, underscore, period only)
  if (cleaned && !/^[a-zA-Z0-9_.]+$/.test(cleaned)) {
    return { valid: false, error: 'Only letters, numbers, underscores, and periods allowed' };
  }
  
  // Check for @ symbol
  if (cleaned.includes('@')) {
    return { valid: false, error: 'Username cannot contain @ symbol' };
  }
  
  // Check for consecutive underscores or periods
  if (/__/.test(cleaned) || /\.\./.test(cleaned)) {
    return { valid: false, error: 'No consecutive underscores or periods' };
  }
  
  // Check for underscore/period at start or end
  if (/^[_.]/.test(cleaned) || /[_.]$/.test(cleaned)) {
    return { valid: false, error: 'Cannot start or end with underscore or period' };
  }
  
  // Check for email-like patterns
  if (/\.(com|net|org|io|app|co)$/i.test(cleaned)) {
    return { valid: false, error: 'Username cannot look like an email' };
  }
  
  // Check for phone numbers (10+ consecutive digits)
  if (/\d{10,}/.test(cleaned)) {
    return { valid: false, error: 'Username cannot contain phone numbers' };
  }
  
  // Check for profanity
  const lowerUsername = cleaned.toLowerCase();
  for (const word of PROFANITY_LIST) {
    if (lowerUsername.includes(word)) {
      return { valid: false, error: 'Username contains inappropriate language' };
    }
  }
  
  // Check reserved usernames
  if (cleaned && isUsernameReserved(cleaned)) {
    return { valid: false, error: 'This username is reserved' };
  }
  
  // Valid!
  if (cleaned.length >= 3 || cleaned.toLowerCase() === 'erik') {
    return { valid: true, error: null };
  }
  
  return { valid: null, error: null }; // Still typing
}

function Auth() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signUp, signIn, signInWithGoogle, signInWithApple, isUsernameReserved } = useAuth();
  
  const [mode, setMode] = useState('login'); // 'login' or 'signup'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [googleDisabled, setGoogleDisabled] = useState(false);
  const [appleDisabled, setAppleDisabled] = useState(false);
  const [usernameValidation, setUsernameValidation] = useState({ valid: null, error: null });
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    username: '',
    displayName: '',
    birthdate: ''
  });

  // Support redirect via location.state (from ProtectedRoute) or query param (from share links)
  const searchParams = new URLSearchParams(location.search);
  const from = location.state?.from?.pathname || searchParams.get('redirect') || '/';

  
  // Validate username in real-time with debounce
  useEffect(() => {
    if (mode !== 'signup' || !formData.username) {
      setUsernameValidation({ valid: null, error: null });
      return;
    }
    
    const timeoutId = setTimeout(async () => {
      // Client-side format check first (fast)
      const result = validateUsername(formData.username, isUsernameReserved);
      if (!result.valid) {
        setUsernameValidation(result);
        return;
      }
      // Server-side uniqueness check
      try {
        const { data, error } = await supabase.rpc('validate_username', {
          username_input: formData.username.trim()
        });
        if (!error && data?.length > 0 && !data[0].is_valid) {
          setUsernameValidation({ valid: false, error: data[0].error_message });
        } else {
          setUsernameValidation({ valid: true, error: null });
        }
      } catch {
        // Network error — fall back to client-only result
        setUsernameValidation(result);
      }
    }, 300);
    
    return () => clearTimeout(timeoutId);
  }, [formData.username, mode, isUsernameReserved]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // For username, auto-trim leading/trailing whitespace and remove spaces
    let processedValue = value;
    if (name === 'username') {
      // Remove @ if user tries to add it
      processedValue = value.replace('@', '');
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
    setError('');
    setSuccessMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      if (mode === 'signup') {
        if (!formData.username.trim()) {
          throw new Error('Username is required');
        }
        
        // Validate username with all rules
        const validation = validateUsername(formData.username.trim(), isUsernameReserved);
        if (!validation.valid) {
          throw new Error(validation.error || 'Invalid username');
        }
        
        if (formData.password.length < 6) {
          throw new Error('Password must be at least 6 characters');
        }
        if (!formData.birthdate) {
          throw new Error('Birthdate is required for age verification');
        }
        
        console.log('Starting signup...');
        const signUpResult = await signUp(
          formData.email,
          formData.password,
          formData.username.trim(),
          formData.displayName.trim() || formData.username.trim(),
          formData.birthdate
        );
        console.log('Signup result:', signUpResult);
        
        // Try auto-login after signup
        try {
          console.log('Auto signing in...');
          const signInResult = await signIn(formData.email, formData.password);
          console.log('Auto signin result:', signInResult);
          trackEvent('user_signed_up', { method: 'email' });
          // Success! Navigate immediately
          setLoading(false);
          navigate(from, { replace: true });
          return;
        } catch (signInErr) {
          // If auto-login fails (email not confirmed), show email confirmation modal
          console.log('Auto-login failed, email confirmation likely required:', signInErr.message);
          trackEvent('user_signed_up', { method: 'email' });
          setLoading(false);
          setPendingEmail(formData.email);
          setShowEmailConfirmation(true);
          return;
        }
      } else {
        console.log('Starting signin...');
        const result = await signIn(formData.email, formData.password);
        console.log('Signin successful:', result);
      }
      
      // Success! Navigate immediately
      console.log('Navigating to:', from);
      trackEvent('user_logged_in', { method: 'email' });
      setLoading(false);
      navigate(from, { replace: true });
      
    } catch (err) {
      console.error('Auth error:', err);
      let friendlyMessage = err.message || 'An error occurred. Please try again.';
      if (friendlyMessage.toLowerCase().includes('email not confirmed')) {
        setPendingEmail(formData.email);
        setShowEmailConfirmation(true);
        setLoading(false);
        return;
      }
      if (friendlyMessage === 'Invalid login credentials') {
        friendlyMessage = 'Invalid email or password. If you haven\'t verified your email, please check your inbox.';
      }
      setError(friendlyMessage);
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    const email = resetEmail || formData.email;
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    setResetLoading(true);
    setError('');
    setSuccessMessage('');
    try {
      const { error } = await supabasePublic.auth.resetPasswordForEmail(email, {
        redirectTo: 'https://gettilted.app/reset-password'
      });
      if (error) throw error;
      setSuccessMessage('Password reset email sent! Check your inbox.');
      setShowForgotPassword(false);
    } catch (err) {
      setError(err.message || 'Failed to send reset email');
    } finally {
      setResetLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');

    try {
      // Store redirect URL before OAuth redirect (localStorage survives the redirect)
      if (from && from !== '/') {
        localStorage.setItem('auth_redirect', from);
      }
      const result = await signInWithGoogle();
      // On native, signInWithGoogle resolves after the deep link callback completes
      // On web, it redirects away so this line is never reached
      if (result) {
        console.log('OAuth sign-in completed, navigating to:', from);
        trackEvent('user_logged_in', { method: 'google' });
        setLoading(false);
        navigate(from, { replace: true });
      }
    } catch (err) {
      console.error('Google auth error:', err);
      if (err.code === OAUTH_CANCELLED) {
        setLoading(false);
        return;
      }
      const errorMsg = err.message || 'Google sign in failed';
      setError(errorMsg);
      // If Google OAuth is not configured, hide the button
      if (errorMsg.includes('not available yet')) {
        setGoogleDisabled(true);
      }
      setLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    setLoading(true);
    setError('');

    try {
      if (from && from !== '/') {
        localStorage.setItem('auth_redirect', from);
      }
      const result = await signInWithApple();
      // On native, signInWithApple resolves after the deep link callback completes
      if (result) {
        console.log('OAuth sign-in completed, navigating to:', from);
        trackEvent('user_logged_in', { method: 'apple' });
        setLoading(false);
        navigate(from, { replace: true });
      }
    } catch (err) {
      console.error('Apple auth error:', err);
      if (err.code === OAUTH_CANCELLED) {
        setLoading(false);
        return;
      }
      const errorMsg = err.message || 'Apple sign in failed';
      setError(errorMsg);
      if (errorMsg.includes('not available yet')) {
        setAppleDisabled(true);
      }
      setLoading(false);
    }
  };

  // Calculate max date (must be 21 years ago)
  const today = new Date();
  const maxDate = new Date(today.getFullYear() - 21, today.getMonth(), today.getDate())
    .toISOString().split('T')[0];

  // Resend confirmation email
  const handleResendConfirmation = async () => {
    if (!pendingEmail) return;
    
    setResendLoading(true);
    setResendSuccess(false);
    
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: pendingEmail,
        options: { emailRedirectTo: getEmailConfirmRedirect() },
      });
      
      if (error) throw error;
      
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (err) {
      console.error('Resend error:', err);
      setError('Failed to resend confirmation email. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  // Close email confirmation modal and go to login
  const handleConfirmationDismiss = () => {
    setShowEmailConfirmation(false);
    setMode('login');
  };

  // Email Confirmation Modal
  if (showEmailConfirmation) {
    return (
      <div className="min-h-screen bg-dive-black flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Email Confirmation Card */}
          <div className="bg-dive-dark border border-dive-gray rounded-2xl p-8 text-center">
            {/* Email Icon */}
            <div className="inline-flex items-center justify-center w-20 h-20 bg-dive-neon/20 rounded-full mb-6">
              <Mail className="w-10 h-10 text-dive-neon" />
            </div>
            
            <h1 className="font-display text-2xl text-white tracking-wide mb-2">
              Check Your Email! 📧
            </h1>
            
            <p className="text-gray-300 mb-4">
              We sent a confirmation link to:
            </p>
            
            <p className="text-dive-neon font-medium text-lg mb-6 break-all">
              {pendingEmail}
            </p>
            
            <div className="bg-dive-darker border border-dive-gray rounded-lg p-4 mb-6 text-left">
              <p className="text-gray-300 text-sm mb-3">
                <strong className="text-white">Click the link in the email</strong> to activate your account. It may take a few minutes to arrive.
              </p>
              <p className="text-gray-500 text-sm">
                💡 Can't find it? Check your spam or promotions folder.
              </p>
            </div>

            {/* Resend Section */}
            <div className="mb-6">
              {resendSuccess ? (
                <div className="flex items-center justify-center gap-2 text-green-400 text-sm">
                  <CheckCircle className="w-4 h-4" />
                  <span>Confirmation email resent!</span>
                </div>
              ) : (
                <button
                  onClick={handleResendConfirmation}
                  disabled={resendLoading}
                  className="text-dive-neon hover:text-dive-neon/80 text-sm flex items-center justify-center gap-2 mx-auto disabled:opacity-50"
                >
                  {resendLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  Resend confirmation email
                </button>
              )}
            </div>
            
            <button
              onClick={handleConfirmationDismiss}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              Got it, take me to login
              <ArrowRight className="w-5 h-5" />
            </button>
            
            <p className="text-gray-500 text-xs mt-6">
              Once confirmed, you can log in and start rating bars!
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-dive-black flex items-start justify-center p-4 overflow-y-auto"
      style={{ paddingTop: 'calc(1rem + env(safe-area-inset-top, 0px))' }}
    >
      <div
        aria-hidden="true"
        className="fixed top-0 left-0 right-0 z-50 bg-dive-black pointer-events-none"
        style={{ height: 'env(safe-area-inset-top, 0px)' }}
      />
      <div className="w-full max-w-md my-auto">
        {/* Logo */}
        <div className="text-center mb-5">
          <img 
            src="/logo-full.png" 
            alt="Tilted" 
            className="w-[180px] sm:w-[240px] max-w-full mx-auto mb-2"
          />
          <p className="text-gray-400">
            The Dive Bar Community App
          </p>
        </div>

        {/* 21+ Warning */}
        <div className="bg-amber-500/10 border border-amber-500/50 rounded-lg p-3 mb-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
          <p className="text-amber-400 text-sm">
            You must be 21 or older to use Tilted
          </p>
        </div>

        {/* Benefits of signing up */}
        {mode === 'signup' && (
          <div className="bg-dive-dark/50 border border-dive-gray rounded-lg p-4 mb-4">
            <p className="text-white text-sm font-medium mb-2">Create an account to:</p>
            <ul className="text-gray-400 text-sm space-y-1.5">
              <li className="flex items-center gap-2">
                <span className="text-dive-neon">✓</span> Rate and review dive bars in your city
              </li>
              <li className="flex items-center gap-2">
                <span className="text-dive-neon">✓</span> Create personal "Want to Try" and custom lists
              </li>
              <li className="flex items-center gap-2">
                <span className="text-dive-neon">✓</span> Add friends and see what they rate
              </li>
              <li className="flex items-center gap-2">
                <span className="text-dive-neon">✓</span> Upload photos and share your experiences
              </li>
            </ul>
          </div>
        )}

        {/* Auth Card */}
        <div className="bg-dive-dark border border-dive-gray rounded-2xl p-6">
          {/* Mode Toggle */}
          <div className="flex bg-dive-darker rounded-lg p-1 mb-6">
            <button
              onClick={() => setMode('login')}
              className={`flex-1 py-2 rounded-md text-sm font-medium transition-all ${
                mode === 'login'
                  ? 'bg-dive-neon text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => setMode('signup')}
              className={`flex-1 py-2 rounded-md text-sm font-medium transition-all ${
                mode === 'signup'
                  ? 'bg-dive-neon text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Sign Up
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg mb-4 text-sm">
              {error}
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="bg-green-500/20 border border-green-500 text-green-400 px-4 py-3 rounded-lg mb-4 text-sm flex items-start gap-2">
              <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>{successMessage}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'signup' && (
              <>
                <div>
                  <label className="text-gray-400 text-sm mb-1 block">Username</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      placeholder="divefan"
                      className={`w-full pl-11 pr-10 ${
                        usernameValidation.valid === true 
                          ? 'border-green-500 focus:border-green-500' 
                          : usernameValidation.valid === false 
                          ? 'border-red-500 focus:border-red-500' 
                          : ''
                      }`}
                      required
                    />
                    {/* Validation indicator */}
                    {formData.username && usernameValidation.valid === true && (
                      <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
                    )}
                    {formData.username && usernameValidation.valid === false && (
                      <XCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-500" />
                    )}
                  </div>
                  {/* Validation message */}
                  {usernameValidation.error && (
                    <p className="text-red-400 text-xs mt-1">{usernameValidation.error}</p>
                  )}
                  {usernameValidation.valid === true && (
                    <p className="text-green-400 text-xs mt-1">Username available!</p>
                  )}
                  <p className="text-gray-500 text-xs mt-1">Letters, numbers, underscores, and periods only</p>
                </div>
                
                <div>
                  <label className="text-gray-400 text-sm mb-1 block">Display Name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type="text"
                      name="displayName"
                      value={formData.displayName}
                      onChange={handleChange}
                      placeholder="Dive Fan (optional)"
                      className="w-full pl-11"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-gray-400 text-sm mb-1 block">
                    Date of Birth <span className="text-amber-400">(21+ required)</span>
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 pointer-events-none z-10" />
                    <input
                      type="date"
                      name="birthdate"
                      value={formData.birthdate}
                      onChange={handleChange}
                      max={maxDate}
                      className="w-full pl-11 pr-3"
                      required
                    />
                  </div>
                </div>
              </>
            )}

            <div>
              <label className="text-gray-400 text-sm mb-1 block">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  className="w-full pl-11"
                  required
                />
              </div>
            </div>

            <div>
              <label className="text-gray-400 text-sm mb-1 block">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="w-full pl-11"
                  required
                  minLength={6}
                />
              </div>
            </div>

            {/* Forgot password link (login only) */}
            {mode === 'login' && !showForgotPassword && (
              <div className="text-right -mt-2">
                <button
                  type="button"
                  onClick={() => { setShowForgotPassword(true); setResetEmail(formData.email); }}
                  className="text-dive-neon text-sm hover:underline"
                >
                  Forgot password?
                </button>
              </div>
            )}

            {/* Forgot password form */}
            {showForgotPassword && (
              <div className="bg-dive-darker border border-dive-gray rounded-lg p-4 space-y-3">
                <p className="text-gray-300 text-sm">Enter your email and we'll send a password reset link.</p>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full pl-11"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    disabled={resetLoading}
                    className="btn-primary flex-1 flex items-center justify-center gap-2 text-sm py-2"
                  >
                    {resetLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Send Reset Link
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(false)}
                    className="px-3 py-2 text-gray-400 text-sm hover:text-white"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Username warning (signup only) */}
            {mode === 'signup' && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                <p className="text-amber-400 text-sm">
                  <strong>⚠️ Choose carefully!</strong> Usernames cannot be changed after signup.
                </p>
              </div>
            )}

            {/* Terms of Service agreement (signup only) */}
            {mode === 'signup' && (
              <div
                className="flex items-start gap-3 min-h-[44px] py-2"
                onClick={() => setAgreedToTerms(!agreedToTerms)}
              >
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-0.5 w-5 h-5 flex-shrink-0 rounded"
                  style={{ accentColor: '#ff2d55', appearance: 'auto', WebkitAppearance: 'checkbox' }}
                />
                <span className="text-gray-300 text-sm leading-5">
                  I agree to the{' '}
                  <Link to="/terms" className="text-dive-neon hover:underline" onClick={(e) => e.stopPropagation()}>Terms of Service</Link>
                  {' '}and{' '}
                  <Link to="/privacy" className="text-dive-neon hover:underline" onClick={(e) => e.stopPropagation()}>Privacy Policy</Link>
                </span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || (mode === 'signup' && (usernameValidation.valid === false || !agreedToTerms))}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  {mode === 'login' ? 'Sign In' : 'Create Account'}
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>

          {/* Continue without account */}
          <div className="mt-4 text-center">
            <Link 
              to="/" 
              className="text-gray-400 hover:text-white text-sm underline"
            >
              Continue to site without an account →
            </Link>
          </div>

          {/* OAuth Sign In Options */}
          {(!googleDisabled || !appleDisabled) && (
            <>
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-dive-gray"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-dive-dark text-gray-500">or continue with</span>
                </div>
              </div>

              <div className="space-y-3">
                {!googleDisabled && (
                  <button
                    onClick={handleGoogleSignIn}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white text-gray-800 rounded-lg font-medium hover:bg-gray-100 transition-colors disabled:opacity-50"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path
                        fill="currentColor"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="currentColor"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    Sign in with Google
                  </button>
                )}

                {!appleDisabled && (
                  <button
                    onClick={handleAppleSignIn}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-black text-white rounded-lg font-medium hover:bg-black/90 transition-colors disabled:opacity-50 border border-gray-700"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                    </svg>
                    Sign in with Apple
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-gray-500 text-sm mt-6">
          By signing up, you confirm you are 21+ and agree to our{' '}
          <Link to="/terms" className="text-dive-neon hover:underline">Terms of Service</Link>
          {' '}&amp;{' '}
          <Link to="/privacy" className="text-dive-neon hover:underline">Privacy Policy</Link>
        </p>
      </div>
    </div>
  );
}

export default Auth;
